import { computed, effect, inject, Signal, signal, WritableSignal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { LayerBloc } from '@connect-services/client/blocs/layer.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import {
    BookmarkedColumnFormType,
    LayerFormType,
} from '@connect-services/client/components/project-settings/edit-layer-template/edit-layer-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { GeomLayer } from '@connect-services/client/models/geom-layer.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    DepositsService,
    DepositWithFolderAndStandard,
    FieldInterventionEntitiesService,
    Layer,
    LayerAttribute,
    LayersService,
    UpdateLayerDto,
} from '@connect-services/client/sdk/api';
import { DetailPanelService } from '@connect-services/client/services/detail-panel.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { labelByLayerType, LayerService } from '@connect-services/client/services/layer.service';
import { LayerEditService } from '@connect-services/client/services/layer-edit.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { ProjectSettingsPermissionsService } from '@connect-services/client/services/project-settings-permissions.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export abstract class AbstractLayerDetailComponent {
    protected readonly activatedRoute = inject(ActivatedRoute);
    protected readonly attributesBookmarked: Signal<LayerAttribute[] | undefined> = computed(() => {
        if (!this.dataAttributes()?.length) {
            return undefined;
        }

        return this.dataAttributes()!.filter((dataAttribute: LayerAttribute) => dataAttribute.isBookmarked);
    });
    protected readonly attributesOther: Signal<LayerAttribute[] | undefined> = computed(() => {
        if (!this.dataAttributes()?.length) {
            return undefined;
        }

        return this.dataAttributes()!.filter((dataAttribute: LayerAttribute) => !dataAttribute.isBookmarked);
    });
    protected readonly canDelete = computed(
        () => this.projectSettingsPermissionsService.canManageProjectLayer() && !this.isLinkedToIntervention(),
    );
    protected readonly canEditSymbology = computed<boolean>(
        () =>
            !this.hasStandard() &&
            [Layer.TypeEnum.WFS, Layer.TypeEnum.TABLE, Layer.TypeEnum.GEOM].includes(this.layerBloc.value()!.type),
    );
    protected readonly dataAttributes: WritableSignal<LayerAttribute[] | null> = signal(null);
    protected readonly deleteTooltip = computed<string | undefined>(() => {
        if (this.canDelete()) {
            return undefined;
        }

        if (!this.projectSettingsPermissionsService.canManageProjectLayer()) {
            return 'insufficientPermissions';
        }

        return 'layerLinkedToInterventionDelete';
    });
    protected readonly depositServiceSdk = inject(DepositsService);
    protected readonly detailPanelService = inject(DetailPanelService);
    protected readonly errorsIndexes: WritableSignal<number[]> = signal([]);
    protected readonly formGroup: WritableSignal<FormGroup<LayerFormType> | undefined> = signal(undefined);
    protected readonly hasStandard = signal(false);
    protected readonly isLinkedToIntervention = signal(true);
    protected readonly isLoading = signal(true);
    protected readonly labelByLayerType = labelByLayerType;
    protected readonly layerBloc = inject(LayerBloc);
    protected readonly layerEditService = inject(LayerEditService);
    protected readonly layerService = inject(LayerService);
    protected readonly layersServiceSdk = inject(LayersService);
    protected readonly router = inject(Router);
    protected readonly snackBarService = inject(SnackBarService);

    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly homeService = inject(HomeService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectSettingsPermissionsService = inject(ProjectSettingsPermissionsService);
    private readonly sourcesService = inject(SourcesService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        effect(async () => {
            if (!this.layerBloc.value()) {
                return;
            }

            await this.setHasStandard();
            await this.setIsLinkedToIntervention();
        });
    }

    public get bookmarkedColumnsControl(): FormArray<FormGroup<BookmarkedColumnFormType>> {
        return this.formGroup()!.controls.bookmarkedColumns!;
    }

    public getUpdateLayerDto(): UpdateLayerDto {
        return {};
    }

    public initFormGroup(): FormGroup<LayerFormType> {
        const formGroup = new FormGroup<LayerFormType>({
            isVisible: new FormControl<boolean | null>(
                this.layerBloc.value()!.hasGeom ? GeomLayer.fromLayer(this.layerBloc.value()!).isVisible : null,
                {
                    nonNullable: this.layerBloc.value()!.hasGeom,
                },
            ),
            name: new FormControl<string>(this.layerBloc.value()!.name, {
                nonNullable: true,
                validators: ValidatorsService.stringValidationForm({
                    maxLength: FormService.VARCHAR_COMMON_LENGTH,
                    minLength: FormService.VARCHAR_MIN_LENGTH,
                }),
            }),
            order: new FormControl<number | null>(
                this.layerBloc.value()!.order ?? (this.layerBloc.value()!.hasGeom ? 1 : null),
                {
                    nonNullable: this.layerBloc.value()!.hasGeom,
                    validators: this.layerBloc.value()!.hasGeom ? [Validators.required] : [],
                },
            ),
        });

        return this.updateFormGroup(formGroup);
    }

    public updateBookmarkedColumns(index: number): void {
        const isBookmarked = this.bookmarkedColumnsControl.controls[index].controls.isBookmarked.value;
        const column = this.bookmarkedColumnsControl.controls[index].controls.column.value;
        const order: number | null = this.bookmarkedColumnsControl.controls[index].controls.order.value;

        this.bookmarkedColumnsControl.controls[index].setValue(
            {
                column,
                isBookmarked: !isBookmarked,
                order: !isBookmarked ? order : null,
            },
            { emitEvent: true },
        );

        this.bookmarkedColumnsControl.controls[index].markAsDirty();
    }

    public updateBookmarkedColumnsOrder(order: number | null, index: number): void {
        this.bookmarkedColumnsControl.controls[index].patchValue({ order }, { emitEvent: true });
        this.errorsIndexes.set([]);

        const errorIndexes: number[] = [];
        if (this.formGroup()!.controls.bookmarkedColumns!.hasError('outOfRangeIndexes')) {
            const indexes: number[] = this.formGroup()!.controls.bookmarkedColumns!.getError('outOfRangeIndexes');
            errorIndexes.push(...indexes);
        }
        if (this.formGroup()!.controls.bookmarkedColumns!.hasError('duplicates')) {
            const indexes: number[] = this.formGroup()!.controls.bookmarkedColumns!.getError('duplicates');
            errorIndexes.push(...indexes);
        }
        if (this.formGroup()!.controls.bookmarkedColumns!.hasError('emptyOrderIndexes')) {
            const indexes: number[] = this.formGroup()!.controls.bookmarkedColumns!.getError('emptyOrderIndexes');
            errorIndexes.push(...indexes);
        }

        const unique = [...new Set(errorIndexes)];
        this.errorsIndexes.set(unique);
    }

    public updateBusinessIdentifier(column: string | null): void {
        this.formGroup()!.controls.businessIdentifier!.setValue(column);
        this.formGroup()!.controls.businessIdentifier!.markAsDirty();
    }

    public updateFormGroup(formGroup: FormGroup<LayerFormType>): FormGroup<LayerFormType> {
        return formGroup;
    }

    public updateVersionLock(): void {
        this.formGroup()!.controls.versionLock!.setValue(!this.formGroup()!.controls.versionLock!.value);
    }

    public updateVisibility(): void {
        this.formGroup()!.controls.isVisible.setValue(!this.formGroup()!.controls.isVisible.value);
    }

    protected async closeDetail(layerId: string): Promise<void> {
        await this.detailPanelService.toggleLayerEntityDetailPanel(layerId);
    }

    protected deleteLayer(layerId: string): void {
        if (!layerId) {
            return;
        }

        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('remove'),
                description: this.translocoService.translate('layerRemovalDescription'),
                title: this.translocoService.translate('layerRemovalConfirmation'),
                type: ModalType.DANGER,
            },
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            try {
                await firstValueFrom(
                    this.layersServiceSdk.layerControllerDelete({
                        id: this.layerBloc.value()!.id,
                    }),
                );

                this.snackBarService.openSuccessSnackBar('layerRemovedSuccess');
                await this.router.navigate([
                    RoutePathEnum.PROJECTS,
                    this.projectBloc.value()!.id,
                    RoutePathEnum.SETTINGS,
                ]);

                this.layerService.forceLayerListRefresh.set(this.layerService.forceLayerListRefresh() + 1);
                await this.homeService.initSources();
            } catch {
                this.snackBarService.openErrorSnackBar('errorDELETE');
            }
        });
    }

    protected getSortedBookmarkedColumnNames(): string[] {
        if (this.formGroup()!.controls.bookmarkedColumns!.pristine) {
            return [];
        }

        return this.formGroup()!
            .value.bookmarkedColumns!.filter(
                (column: Partial<{ column: string; isBookmarked: boolean; order: number | null }>) =>
                    column.isBookmarked && column.column,
            )
            .sort(
                (
                    a: Partial<{ column: string; isBookmarked: boolean; order: number | null }>,
                    b: Partial<{ column: string; isBookmarked: boolean; order: number | null }>,
                ) => (a.order || 0) - (b.order || 0),
            )
            .map(
                (bookmarks: Partial<{ column: string; isBookmarked: boolean; order: number | null }>) =>
                    bookmarks.column as string,
            );
    }

    protected async setDataAttributes(): Promise<void> {
        this.isLoading.set(true);

        const filteredAttributes = await this.layerService.getAttributes(this.layerBloc.value()!);

        this.dataAttributes.update(() => [...filteredAttributes]);
        this.isLoading.set(false);
    }

    protected async setIsLinkedToIntervention(): Promise<void> {
        const entities = await firstValueFrom(
            this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGet({
                layers: [this.layerBloc.value()!.id],
            }),
        );

        this.isLinkedToIntervention.set(!!entities.items?.length);
    }

    protected async submit(): Promise<void> {
        if (this.formGroup()!.invalid) {
            return;
        }

        const editedLayer: UpdateLayerDto = this.getUpdateLayerDto();
        await this.layerEditService.editLayer(this.layerBloc.value()!.id, editedLayer);

        const isVisibleCtrl = this.formGroup()!.controls.isVisible;
        if (!isVisibleCtrl.dirty) {
            return;
        }
        const source = this.sourcesService.sources().find((source) => source.sourceId === this.layerBloc.value()!.id);
        if (!source) {
            return;
        }

        this.mapSourceService.toggleSourceVisibility(source);
    }

    private async setHasStandard(): Promise<void> {
        if (!this.layerBloc.value()!.deposit) {
            return;
        }

        const deposit: DepositWithFolderAndStandard = await firstValueFrom(
            this.depositServiceSdk.depositControllerGetDepositWithFolderAndStandard({
                id: this.layerBloc.value()!.deposit!,
            }),
        );

        this.hasStandard.set(!!deposit?.standard);
    }
}
